/*    
<summary>
   This class component is all about Managing store data functionality.
   Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from "mobx";
import { initialState } from "../initialState/add-edit-store-state";
import { initialState as GetAllStoreManagementState } from "../initialState/get-all-store-state";
import { AuthStore } from "./auth-store";
import { ICommonState } from "../../models/state/ICommonState";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import * as baseService from "../service/base-service";
import { formatMessage } from "../../translations/format-message";
import toast from "react-hot-toast";
import {
  IStoreManagementList,
  IStoreManagementListVM,
  IStoreManagementVM,
} from "../../models/response/IStoreManagementResponse";
import URLConstants from "../../constants/url-constants";
import { getBusinessTypeIdFromLocalStorage, getHeadquarterIdFromLocalStorage, getLanguageFromLocalStorage, getUserTypeFromLocalStorage } from "../../helpers/localstorage-helper";
import { emptyValue, safeBase64Encode } from "../../constants/constants";
import { IAddStoreManagement, IUpdateStoreManagement } from "../../models/forms/IAddStore";
import { demoStoreData } from "./data";
import base64 from "base-64";
import {
  IStaffFilter,
  IStoreFilter,
  IStoreInitialFilterState,
} from "../../models/IFilter";
import { storeInitialFilterState } from "../initialState/initial-filter-state";
import { IPrefectureZipCode, IPrefectureZipCodeVM } from "../../models/response/IStoreZipCodeResponse";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { toHalfWidth } from "../../helpers/validate-helper";

const authStore = new AuthStore();

export class StoreManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  storeList: IStoreManagementListVM = GetAllStoreManagementState;

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateStoreManagementState = { ...this.initialStateValue };
  deleteStoreManagementState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };

  zipCodeData: IPrefectureZipCode | undefined = undefined;
  store: IStoreManagementVM | undefined = undefined;
  storeState = { ...this.initialStateValue };
  storeFilter: IStoreFilter = { ...storeInitialFilterState };
  allprefectureOptionsState = { ...this.initialStateValue };
  allprefectureOptions:any = [];

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      storeList: observable,
      store: observable,
      storeFilter: observable,
      zipCodeData: observable,
      addUpdateStoreManagementState: observable,
      deleteStoreManagementState: observable,
      storeState: observable,
      allprefectureOptionsState: observable,
      allprefectureOptions: observable,
      GetStoreManagementListService: action,
      GetPrefectureUsingPostalCode: action,
      GetStoreManagementFreewordFilterService: action,
      AddStoreManagementService: action,
      UpdateStoreManagementService: action,
      UpdateStoreManagementStatusService: action,
      DeleteStoreManagementService: action,
      GetStoreManagementByIdService: action,
      GetAllPrefectureOptionService: action,
      GetStoreRegistrationByBusinessTypeIdService: action,
      reset: action,
      resetStore: action,
      resetPrefectureByZipCode: action,
      setStoreFilterDetail: action,
      resetStoreManagementDetail: action,
      resetAddUpdateStoreManagementState: action,
      resetStoreFilterDetail: action,
      allStoreManagement: computed,
      getStoreFilter: computed,
      storeDetail: computed,
      prefectureByZipCode: computed,
      getAllStoreManagementPrefecture: computed,
      getAllPrefectureOptions: computed
    });
  }

  /**
   * This function is used to Get All StoreManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetStoreManagementListService = (
    currentPage: number,
    pagerSize: number,
    column: string,
    orderBy: string,
    filter: any
  ) => {
    this.inProgress = true;
    let businessTypeId = getBusinessTypeIdFromLocalStorage();
    const userType = getUserTypeFromLocalStorage();

    if (userType === userTypeEnum[userTypeEnum.HeadQuarter]) {
      if (businessTypeId !== this.getStoreFilter.BusinessTypeId)
        this.setStoreFilterDetail({
          ...this.getStoreFilter,
          BusinessTypeId: businessTypeId,
        });
    }

    const url =
      Number(this.storeFilter.BusinessTypeId) === -1
        ? URLConstants.StoreFilter +
        "?PageNo=" +
        currentPage +
        "&PageSize=" +
        pagerSize +
        "&column=" +
        column +
        "&orderBy=" +
        orderBy +
        "&is_invalid=" +
        this.storeFilter.IsInvalid +
        "&prefectureName=" +
        this.storeFilter.PrefectureName
        : URLConstants.StoreFilter +
        "?PageNo=" +
        currentPage +
        "&PageSize=" +
        pagerSize +
        "&businessTypeId=" +
        this.storeFilter.BusinessTypeId +
        "&column=" +
        column +
        "&orderBy=" +
        orderBy +
        "&is_invalid=" +
        this.storeFilter.IsInvalid +
        "&prefectureName=" +
        this.storeFilter.PrefectureName
    // this.storeList = demoStoreData;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IStoreManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.storeList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetStoreManagementFreewordFilterService = (
    currentPage: number,
    pagerSize: number,
    searchText: string
  ) => {
    this.inProgress = true;

    let businessTypeId = -1;
    const loggedInUserType = getUserTypeFromLocalStorage();

    if (userTypeEnum[userTypeEnum.HeadQuarter] === loggedInUserType) {
      businessTypeId = getBusinessTypeIdFromLocalStorage() ?? -1;
    }

    const urlParams = new URLSearchParams();
    urlParams.append('PageNo', String(currentPage));
    urlParams.append('PageSize', String(pagerSize));
    urlParams.append('Search', String(searchText));
    if (businessTypeId > 0) {
      urlParams.append('businessTypeId', String(businessTypeId));
    }

    const url =
      URLConstants.StoreSearchFilter + "?" + urlParams.toString();
    // this.storeList = demoStoreData;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IStoreManagementListVM>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.storeList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map storeList to allStoreManagement List suitable for Grid Component.
   * @returns allStoreManagementsList
   */
  get allStoreManagement(): IStoreManagementList[] {
    if (this.storeList?.StoreVM && this.storeList.StoreVM?.length > 0)
      return this.storeList.StoreVM.map((store) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;
        return {
          Id: store.Id,
          StoreName: store.StoreName,
          StoreCode: store.StoreCode,
          BusinessTypeId: store.BusinessTypeId,
          MobileNumber: store.MobileNumber,
          Language: language,
          Email: store.Email,
          PrefectureName: store.PrefectureName ? store.PrefectureName : emptyValue,
          PostalCode: store.PostalCode,
          StoreOwnerName: store.StoreOwnerName,
          Address: store.Address,
          Url: store.Url,
          KanaName: store.KanaName,
          Status: store.Status,
          IsNominationFee: store.IsNominationFee,
          StoreCategory: store.StoreCategory,
          StoreLink: store.StoreLink,
          Image: store.Image,
          ThumbnailImage: store.ThumbnailImage,
          isDelete: isDelete,
          isEdit: isEdit,
        };
      });
    return [];
  }

  get getAllStoreManagementPrefecture(): any[] {
    if (this.storeList?.StoreVM && this.storeList.StoreVM?.length > 0) {
      // Create a Set to store unique prefecture names
      const uniquePrefectures = new Set<string>();

      // Iterate over the storeList and add prefecture names to the Set
      this.storeList.StoreVM.forEach((store) => {
        if (store?.PrefectureName) {
          uniquePrefectures.add(store.PrefectureName);
        }
      });

      // Convert the Set to an array of objects with id and value
      return Array.from(uniquePrefectures).map((prefecture, index) => ({
        id: index + 1, // Generate a unique ID (or use any other logic for ID generation)
        value: prefecture,
      }));
    }

    return [];
  }

  /**
   * This function is used to Get StoreManagement Details by Id by calling an API.
   * @param id : The StoreManagement Identifier
   * @returns
   */
  GetStoreManagementByIdService = (id: number) => {
    //   let storeDetail = demoStoreData.Stores.filter((store:any)=> store.Id === id);
    //   if(storeDetail.length > 0){
    //     this.store = storeDetail[0];
    //   }
    this.storeState.inProgress = true;
    let url = `${URLConstants.Store}/${id}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IStoreManagementVM>>) => {
          if (response.data.Error) {
            this.storeState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.store = data;
          }
        }
      )
      .catch((err: string) => {
        this.storeState.error = err;
      })
      .finally(
        action(() => {
          this.storeState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add StoreManagement Form.
   * @returns StoreManagement Details
   */
  get storeDetail() {
    if (this.store) {
      return {
        Id: this.store.Id,
        StoreName: this.store.StoreName ? this.store.StoreName : "",
        StoreCode: this.store.StoreCode ?? "",
        BusinessTypeId: this.store.BusinessTypeId
          ? this.store.BusinessTypeId
          : -1,
        MobileNumber: this.store.MobileNumber ? this.store.MobileNumber : "",
        Email: this.store.Email ? this.store.Email.toLowerCase() : "",
        PrefectureName: this.store.PrefectureName ? this.store.PrefectureName : "",
        PostalCode: this.store.PostalCode ? this.store.PostalCode : "",
        StoreOwnerName: this.store.StoreOwnerName
          ? this.store.StoreOwnerName
          : "",
        Address: this.store.Address ? this.store?.PrefectureName ? this.store?.PrefectureName + this.store.Address : this.store.Address : "",
        Url: this.store.Url ? this.store.Url : "",
        KanaName: this.store.KanaName ? this.store.KanaName : "",
        Status: this.store.Status ? this.store.Status.toString() : "0",
        IsNominationFee: this.store.IsNominationFee === true ? "1" : "0",
        StoreCategory: this.store.StoreCategory.toString(),
        StoreLink: this.store.StoreLink ? this.store.StoreLink : "",
        Image: this.store.Image ? this.store.Image : "",
        HeadquarterId: this.store.HeadquarterId,
        ThumbnailImage: this.store.ThumbnailImage
          ? this.store.ThumbnailImage
          : "",
        UserId: this.store.Users ? this.store.Users.Id : 0,
      };
    }
    return {
      ...initialState, BusinessTypeId: getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.HeadQuarter]
        ? getBusinessTypeIdFromLocalStorage() : -1
    };
  }

  GetPrefectureUsingPostalCode = (postalCode: string) => {
    this.storeState.inProgress = true;
    let PostalCode = toHalfWidth(postalCode);
    this.zipCodeData = undefined;
    let url = `${URLConstants.StorePrefecturePostalCode}?PostalCode=${PostalCode}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<any>>) => {
          if (response.data.Error) {
            this.storeState.error = response.data.Message;
          } else {
            let data = response.data.Data;
            this.zipCodeData = data;
            if (data.Results == null) {
              toast.error(formatMessage("invalid_postal_code"));
            }
          }
        }
      )
      .catch((err: string) => {
        this.storeState.error = err;
      })
      .finally(
        action(() => {
          this.storeState.inProgress = false;
        })
      );
  }

  get prefectureByZipCode() {
    if (this.zipCodeData && this.zipCodeData?.Results?.length > 0) {
      // return this.zipCodeData?.Results?.map((obj:IPrefectureZipCodeVM) => {
      return {
        Address1: this.zipCodeData?.Results[0]?.Address1,
        Address2: this.zipCodeData?.Results[0]?.Address2,
        Address3: this.zipCodeData?.Results[0]?.Address3,
        Kana1: this.zipCodeData?.Results[0]?.Kana1,
        Kana2: this.zipCodeData?.Results[0]?.Kana2,
        Kana3: this.zipCodeData?.Results[0]?.Kana3,
        Prefcode: this.zipCodeData?.Results[0]?.Prefcode,
        Zipcode: this.zipCodeData?.Results[0]?.Zipcode
      }
      // })
    }
    return null;
  }

  resetPrefectureByZipCode = () => {
    this.zipCodeData = undefined;
  }

  /*
    This function is used to reset all store observables to their initial values.  
    */
  resetStoreManagementDetail = () => {
    this.store = undefined;
    this.storeState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Add New StoreManagement by calling an API.
   * @param data : StoreManagement Data
   * @returns
   */
  AddStoreManagementService = (data: IAddStoreManagement) => {
    this.addUpdateStoreManagementState.inProgress = true;
    let url = URLConstants.Store;
    const formData = new FormData();
    formData.append("StoreName", data.StoreName);
    formData.append("StoreCode", data.StoreCode);
    formData.append("StoreOwnerName", data.StoreOwnerName);
    formData.append("BusinessTypeId", data.BusinessTypeId.toString());
    formData.append("MobileNumber", String(data.MobileNumber));
    formData.append("Email", data.Email.toLowerCase());
    formData.append("PostalCode", String(data.PostalCode));
    formData.append("Url", data.Url);
    formData.append("KanaName", data.KanaName);
    formData.append("PrefectureName", data.PrefectureName);
    formData.append("Address", data.Address);
    formData.append("Status", data.Status.toString());
    formData.append("Password", safeBase64Encode(data.Password!));
    formData.append(
      "IsNominationFee",
      data.IsNominationFee === "0" ? "false" : "true"
    );
    formData.append("StoreCategory", data.StoreCategory.toString());
    formData.append("StoreLink", data.StoreLink);
    formData.append("Image", data.Image);
    let headquarterId = getHeadquarterIdFromLocalStorage();
    if (userTypeEnum[userTypeEnum.HeadQuarter] === getUserTypeFromLocalStorage() && headquarterId !== -1)
      formData.append("HeadquarterId", headquarterId?.toString());
    formData.append("Image", data.Image);
    if (data.ThumbnailImage)
      formData.append("ThumbnailImage", data.ThumbnailImage);
    return baseService
      .postMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreManagementState.error = response.data.Message;
        } else this.addUpdateStoreManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateStoreManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StoreManagement Details by calling an API.
   * @param id : The StoreManagement Identifier
   * @param data
   * @returns
   */
  UpdateStoreManagementService = (data: IUpdateStoreManagement) => {
    this.addUpdateStoreManagementState.inProgress = true;
    let url = URLConstants.Store;
    const formData = new FormData();
    formData.append("Id", data.Id?.toString());
    if (data.UserId)
      formData.append("UserId", data.UserId?.toString());
    formData.append("StoreName", data.StoreName);
    // formData.append("StoreCode", data.StoreCode);
    formData.append("StoreOwnerName", data.StoreOwnerName);
    formData.append("BusinessTypeId", data.BusinessTypeId?.toString());
    formData.append("MobileNumber", String(data.MobileNumber));
    formData.append("Email", data.Email.toLowerCase());
    formData.append("PostalCode", String(data.PostalCode));
    formData.append("StoreLink", data.StoreLink);
    formData.append("Url", data.Url);
    formData.append("KanaName", data.KanaName);
    formData.append("PrefectureName", data.PrefectureName);
    formData.append("Address", data.Address);
    formData.append("IsPasswordUpdated", data.IsPasswordUpdated ? "true" : "false");
    if (data.IsPasswordUpdated) {
      formData.append("Password", safeBase64Encode(data?.Password ?? ""));
    }
    formData.append("Status", data.Status?.toString());
    formData.append(
      "IsNominationFee",
      data.IsNominationFee === "0" ? "false" : "true"
    );
    formData.append("StoreCategory", data.StoreCategory?.toString());
    formData.append("Image", data.Image);
    if (data.HeadquarterId)
      formData.append("HeadquarterId", data.HeadquarterId?.toString());
    if (data.ThumbnailImage)
      formData.append("ThumbnailImage", data.ThumbnailImage);
    return baseService
      .putMultipart(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateStoreManagementState.error = response.data.Message;
        } else this.addUpdateStoreManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateStoreManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateStoreManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStoreManagement observables to their initial values.  
    */
  resetAddUpdateStoreManagementState = () => {
    this.addUpdateStoreManagementState.inProgress = false;
    this.addUpdateStoreManagementState.success = false;
    this.addUpdateStoreManagementState.error = "";
  };

  /**
   * This function is used to Delete StoreManagement by calling an API.
   * @param id : The StoreManagement Identifier
   * @returns
   */
  DeleteStoreManagementService = (id: number) => {
    this.deleteStoreManagementState.inProgress = true;
    let url = `${URLConstants.Store}/${id}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteStoreManagementState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteStoreManagementState.success = true;
      })
      .catch((err: string) => {
        this.deleteStoreManagementState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteStoreManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStoreManagement observables to their initial values.  
    */
  resetDeleteStoreManagement = () => {
    this.deleteStoreManagementState.inProgress = false;
    this.deleteStoreManagementState.success = false;
    this.deleteStoreManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StoreManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StoreManagement Identifier
   * @param state: StoreManagement state
   * @returns
   */
  UpdateStoreManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.StoreManagementStatus;
    let storeData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, storeData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  setStoreFilterDetail = (filter: IStoreFilter) => {
    this.storeFilter = { ...filter };
  };

  resetStoreFilterDetail = () => {
    this.storeFilter = { ...storeInitialFilterState };
  };

  get getStoreFilter(): IStoreFilter {
    return this.storeFilter;
  }

  GetStoreRegistrationByBusinessTypeIdService = (
    currentPage: number,
    pagerSize: number,
    businessTypeIds: number[]
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.StoreByBusinessTypeId +
      `?PageNo=${currentPage}&PageSize=${pagerSize}` +
      businessTypeIds.map(id => `&businessTypeId=${id}`).join("");
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IStoreManagementListVM>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.storeList = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
     * This function is used to Get All Generals List data with pagination information by calling an API.
     * @returns
     */
    GetAllPrefectureOptionService = (businessTypeId?: number) => {
      this.allprefectureOptionsState.inProgress = true;
      const url = URLConstants.StoreAllPrefectureName;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<any[]>>) => {
          if (response.data.Error) {
            this.allprefectureOptionsState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.allprefectureOptions = response.data.Data;
          }
        })
        .catch((err: string) => {
          this.allprefectureOptionsState.error = err;
        })
        .finally(
          action(() => {
            this.allprefectureOptionsState.inProgress = false;
          })
        );
    };
  
    /**
     * This function is used to map allprefectureOptions to allGeneral List suitable for Grid Component.
     * @returns getAllPrefectureOptions
     */
    get getAllPrefectureOptions(): IOption[] {
      const options: IOption[] = [
        {
          id: -1,
          value: "please_select",
          disabled: false,
        },
      ];
      if (this.allprefectureOptions && this.allprefectureOptions?.length > 0)
        this.allprefectureOptions.map((option: any, index:number) => {
          options.push({
            id: index,
            value: option.PrefectureName?.toString(),
            disabled: false,
          });
        });
      return options;
    }

    resetAllprefectureOptionsState = () =>{
      this.allprefectureOptions = [];
      this.allprefectureOptionsState = {...this.initialStateValue};
    }

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateStoreManagementState.success = false;
    this.addUpdateStoreManagementState.error = "";
    this.deleteStoreManagementState = { ...this.initialStateValue };
    this.addUpdateStoreManagementState.inProgress = false;
    this.storeState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.error = "";
    this.inProgress = false;
    this.storeList = GetAllStoreManagementState;

    this.addUpdateStoreManagementState = { ...this.initialStateValue };
    this.deleteStoreManagementState = { ...this.initialStateValue };
    this.store = undefined;
    this.storeState = { ...this.initialStateValue };
    this.storeFilter = { ...storeInitialFilterState }
  };
}

export default new StoreManagementStore();
